import { createBrowserHistory } from 'history';
import { useContext } from 'react';
import { __RouterContext } from 'react-router';
import queryString from 'query-string';
import { TimeFrame } from '../util/util';
import { FilterTag, serializeQuery, parseQuery } from './filter';

export const history = createBrowserHistory();

export const useRouterParam = <T extends object>(): T => {
  return useContext(__RouterContext).match.params as T;
};

export const useSearchParam = (paramName: string): string | null => {
  const { history } = useContext(__RouterContext);
  const searchParams = new URLSearchParams(history.location.search);
  const param = searchParams.get(paramName);
  return param;
};

export type SortDirection = 'asc' | 'desc' | '';

interface PageState {
  status?: string[];
  deliveryStatus?: string[];
  storageLocation?: string[];
  timeframe?: TimeFrame;
  query?: FilterTag[];
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDir?: SortDirection;
  shopId?: string;
  withShippableProductsOnly?: 'true' | 'false';
  paymentMethod?: string[];
  archived?: 'true' | 'false' | 'all';
  productId?: string;
  productVariantId?: string;
  venueSectionId?: string;
  itemStatus?: string;
  startDate?: string;
  endDate?: string;
  productStatuses?: string;
  categories?: string;
  onlyActive?: string;
  availability?: string;
  discountedQuery?: FilterTag[];
  discountedCategories?: string[];
  containingOnly?: string;
}

interface QueryParams {
  status?: string;
  storageLocation?: string;
  query?: string;
  startDate?: string;
  endDate?: string;
  page?: string;
  perPage?: string;
  sortBy?: string;
  sortDir?: SortDirection;
  paymentMethod?: string;
  archived?: 'true' | 'false' | 'all';
  availability?: string;
  discountedQuery?: string;
  discountedCategories?: string;
  containingOnly?: string;
}

export const useQueryAsState = (): [PageState, (q: Partial<PageState>) => void] => {
  const params: QueryParams = queryString.parse(history.location.search);

  const parsedParams: PageState = {
    ...params,
    query: parseQuery(params.query),
    status: params.status?.split(',') || undefined,
    storageLocation: (params.storageLocation && params.storageLocation.split(',')) || undefined,
    page: (params.page && params.page !== '0' && parseInt(params.page, 10)) || undefined,
    perPage: (params.perPage && params.perPage !== '10' && parseInt(params.perPage, 10)) || undefined,
    sortBy: params.sortBy,
    sortDir: params.sortDir,
    paymentMethod: params.paymentMethod?.split(',') || undefined,
    archived: params.archived || undefined,
    startDate: params.startDate === 'null' ? undefined : params.startDate,
    endDate: params.endDate === 'null' ? undefined : params.endDate,
    availability: params.availability,
    discountedQuery: parseQuery(params.discountedQuery),
    discountedCategories: params.discountedCategories?.split(',') || undefined,
    containingOnly: params.containingOnly,
  };

  const dateRange: TimeFrame = [
    parsedParams.startDate ? new Date(parseInt(parsedParams.startDate, 10)) : undefined,
    parsedParams.endDate ? new Date(parseInt(parsedParams.endDate, 10)) : undefined,
  ];

  parsedParams.timeframe = dateRange.some(Boolean) ? dateRange : undefined;

  const updateParams = (q: PageState): void => {
    const newParams = { ...parsedParams, ...q };

    const serializedParams: Record<string, unknown> = {
      ...newParams,
      query: (newParams.query && serializeQuery(newParams.query, true)) || undefined,
      page: (!q.perPage && newParams.page) || undefined,
      status: (newParams.status && newParams.status.length > 0 && newParams.status.join(',')) || undefined,
      startDate: newParams.timeframe && newParams.timeframe[0] && newParams.timeframe[0].getTime(),
      endDate: newParams.timeframe && newParams.timeframe[1] && newParams.timeframe[1].getTime(),
      storageLocation:
        (newParams.storageLocation && newParams.storageLocation.length > 0 && newParams.storageLocation.join(',')) ||
        undefined,
      paymentMethod:
        (newParams.paymentMethod && newParams.paymentMethod.length > 0 && newParams.paymentMethod.join(',')) ||
        undefined,
      discountedQuery: (newParams.discountedQuery && serializeQuery(newParams.discountedQuery, true)) || undefined,
      discountedCategories:
        (newParams.discountedCategories &&
          newParams.discountedCategories.length > 0 &&
          newParams.discountedCategories.join(',')) ||
        undefined,
      containingOnly: newParams.containingOnly,
    };

    delete serializedParams.timeframe;

    Object.keys(serializedParams).forEach(
      (key) => (serializedParams[key] === undefined || serializedParams[key] === '') && delete serializedParams[key]
    );

    const stringfiedParam = Object.keys(serializedParams)
      .filter(Boolean)
      .map((key) => `${key}=${serializedParams[key]}`)
      .join('&');

    history.push(`${history.location.pathname}?${stringfiedParam}`);
  };

  return [parsedParams, updateParams];
};
